/**
 * <ticket-wallet-actions> — one ticket's own keep-it control: an icon-button
 * mirrored to the right edge of the same band that carries order-access.view.ts's
 * handover button, opening an overlay that carries THIS ticket's two takeaway
 * artifacts — the Apple and Google Wallet passes, each vendor's own artwork
 * unaltered and never a fabricated look-alike (both vendors forbid recreating
 * their own mark), and the card itself as an image file. Rendered outside
 * <ticket-card>'s shadow root — a sibling of the handover button, not a
 * descendant of the card — so it can never reach the rasterized share capture
 * node.
 *
 * The ticket-level half of the pair whose order-level half is the header
 * strip's own row: that row offers the whole order at once, while this offers
 * just the one card's own, for the buyer forwarding a single ticket rather
 * than taking the lot. Past the ceiling no order-wide control can take the
 * order in one gesture (order-access.state.ts's WALLET_BULK_PASS_CEILING),
 * both the wallet row and "download all" withdraw together, and this control
 * is then the entire takeaway surface — which is the one reading of it that
 * is load-bearing rather than convenient.
 *
 * Renders on any order carrying more than one ticket: at one ticket the order
 * IS the ticket, so the header strip's own controls already are the
 * ticket-level surface and a second control there would cost a tap to answer
 * a question the buyer can't ask. There is no second term. The image is
 * rasterized from the card the buyer is looking at, so it is available on
 * every session and every deployment, wallet or no wallet — a vendor slot
 * that will never fill costs its own badge inside the overlay, never the
 * control that opens it. A pass still being signed likewise keeps the trigger
 * live rather than disabling it: the badge behind it reserves its own box,
 * and the image beside it is already there. A non-"valid" ticket disables the
 * trigger outright, the same treatment the band's own handover button gets —
 * the door will reject that ticket, and handing on a copy of it would mislead
 * whoever receives it.
 *
 * <apple-wallet-badge>/<google-wallet-badge> carry their own per-ticket mode
 * (a `ticketId` property) — this component mounts them scoped to `ticket`
 * rather than duplicating either badge's own vendor-compliance rendering.
 *
 * Saving the image is asked for, never performed here: the capture mounts an
 * off-screen twin of the card and rasterizes it, and order-access.view.ts is
 * the one place that does it — for this control and for its own order-wide
 * chip alike, so both files' images come off one rendering. The ask and the
 * wait both travel on order-access.state.ts's own signals
 * (`saveTicketImages$`, `savingTicketImage`), so this element takes no
 * property and dispatches no event for either: the overlay's save control reads its own request source, and the
 * overlay stays open through it rather than dismissing itself, which is what
 * lets the buyer watch the one thing they asked for finish.
 *
 * Zero I/O — reads only order-access.state.ts's own singletons, same as
 * every other role under packages/views/src.
 */
import { tooltip } from "@mhmo91/schmancy";
import { SchmancyElement, schmancyElement } from "@mhmo91/schmancy/mixins";
import { show } from "@mhmo91/schmancy/overlay";
import { html, type TemplateResult } from "lit";
import { property } from "lit/decorators.js";
import { takeUntil } from "rxjs";
import { LocalizeMixin } from "../../i18n/localize.ts";
// Side-effect: registers <apple-wallet-badge>.
import "./apple-wallet-badge.view.ts";
// Side-effect: registers <google-wallet-badge>.
import "./google-wallet-badge.view.ts";
import {
	failedTicketImageSave,
	type OrderAccessTicket,
	saveTicketImages$,
	savingTicketImage,
	walletMultiTicket,
} from "./order-access.state.ts";

@schmancyElement("ticket-wallet-actions")
export class TicketWalletActionsView extends LocalizeMixin(SchmancyElement) {
	@property({ attribute: false }) ticket!: OrderAccessTicket;

	@property({ attribute: false }) orderAccessToken!: string;

	/** A factory template, not an eager one: it is called at mount time and
	 *  again whenever a signal it reads changes, so the save control below
	 *  carries the live wait rather than a snapshot frozen where this template
	 *  was written. */
	private openTicketActions(): void {
		show(
			() => html`
			<div class="flex flex-col gap-3 p-4">
				<schmancy-typography type="label" token="lg" class="text-surface-onVariant">
					${this.ticket.typeName}
				</schmancy-typography>
				<div class="flex flex-wrap items-center gap-3">
					<apple-wallet-badge .ticketId=${this.ticket.ticketId}></apple-wallet-badge>
					<google-wallet-badge
						.ticketId=${this.ticket.ticketId}
						.orderAccessToken=${this.orderAccessToken}
					></google-wallet-badge>
				</div>
				<schmancy-button
					variant="filled tonal"
					color=${failedTicketImageSave.value?.sourceTicketId === this.ticket.ticketId ? "error" : "neutral"}
					size="sm"
					width="full"
					justify="start"
					?disabled=${savingTicketImage.value !== null}
					.busy=${this.savingThisTicket()}
					@click=${() => {
						if (savingTicketImage.value !== null) return;
						saveTicketImages$.next({
							ticketIds: [this.ticket.ticketId],
							sourceTicketId: this.ticket.ticketId,
						});
					}}
				>
					<schmancy-icon slot="prefix">
						${this.savingThisTicket() ? "hourglass_top" : "download"}
					</schmancy-icon>
<span class="grid min-w-0">
            <span class="invisible col-start-1 row-start-1" aria-hidden="true">${this.t("storefront.orderAccess.ticketActions.saveImage")}</span>
            <span class="invisible col-start-1 row-start-1" aria-hidden="true">${this.t("storefront.orderAccess.ticketActions.retryImage")}</span>
            <span class="col-start-1 row-start-1">${this.t(failedTicketImageSave.value?.sourceTicketId === this.ticket.ticketId ? "storefront.orderAccess.ticketActions.retryImage" : "storefront.orderAccess.ticketActions.saveImage")}</span>
          </span>
				</schmancy-button>
			</div>
		`,
		)
			.pipe(takeUntil(this.disconnecting))
			.subscribe();
	}

	/** Whether the run in flight is THIS card's — read in two places, and the
	 *  one term that separates the card being saved from the cards waiting
	 *  their turn behind it. */
	private savingThisTicket(): boolean {
		return savingTicketImage.value?.sourceTicketId === this.ticket.ticketId;
	}

	override render(): TemplateResult {
		if (!walletMultiTicket.get()) return html``;

		const voided = this.ticket.status !== "valid";
		const ariaLabel = voided
			? this.t("storefront.orderAccess.ticketActions.unavailableAriaLabel", {
					typeName: this.ticket.typeName,
				})
			: this.t("storefront.orderAccess.ticketActions.ariaLabel", {
					typeName: this.ticket.typeName,
				});

		return html`
			<schmancy-icon-button
				icon="download"
				size="sm"
				variant="filled tonal"
				color="neutral"
				class="shadow-md"
				?disabled=${voided}
				aria-label=${ariaLabel}
				${tooltip(ariaLabel)}
				@click=${() => this.openTicketActions()}
			></schmancy-icon-button>
		`;
	}
}

declare global {
	interface HTMLElementTagNameMap {
		"ticket-wallet-actions": TicketWalletActionsView;
	}
}
